import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LeftOutlined } from "@ant-design/icons";
import { Button, Card, notification, Row, Col } from "antd";
import Icon from "@ant-design/icons";
import { LeftIcon, TrolleyIcon } from "../../assets/icons/icons";
import Header from "../../components/layout/header";
import SuccessAlert from "../../components/mobile/SuccessAlert";
import { useRegisterTrolley } from "../common/mutations";
import Styles from "./registerTrolleyReview.module.scss";

const RegisterTrolleyReview = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const trolleyId = searchParams.get("trolleyId") || "";
  const mdu = searchParams.get("mdu") || "";
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [registeredTrolleyId, setRegisteredTrolleyId] = useState<string | null>(null);

  const registerMutation = useRegisterTrolley();

  const handleRegister = async () => {
    // MDU is required - if missing, redirect back
    if (!mdu || !mdu.trim()) {
      notification.error({
        message: "MDU Required",
        description: "MDU is required to register trolley. Please go back and enter MDU.",
        duration: 5,
      });
      navigate(-1);
      return;
    }

    try {
      const response = await registerMutation.mutateAsync({
        trolleyId,
        mdu: mdu.trim(),
      });
      // Store the trolley_id from the response (use response trolley_id or fallback to input trolleyId)
      const trolleyIdFromResponse = response?.trolley_id || trolleyId;
      setRegisteredTrolleyId(trolleyIdFromResponse);
      
      // Clear sessionStorage after successful registration
      const STORAGE_KEY = `registerTrolleyFormData_${trolleyId}`;
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        console.warn("Failed to clear form data from sessionStorage", error);
      }
      
      setIsSuccessOpen(true);
    } catch {
      notification.error({
        message: "Failed to register the Trolley. Please try again later.",
        description: "An error occurred while registering the trolley",
        duration: 5,
      });
    }
  };

  const handleSuccessClose = () => {
    setIsSuccessOpen(false);

    // Use the trolley_id from registration response if available, otherwise fall back to input trolleyId
    const idToNavigate = registeredTrolleyId || trolleyId;

    const PENDING_LOCATION_TROLLEY_KEY = "pendingLocationTrolleyContext";
    const PENDING_TROLLEY_LOAD_KEY = "pendingTrolleyLoadContext";

    // Check for pending location trolley context first
    try {
      const locationStored = sessionStorage.getItem(PENDING_LOCATION_TROLLEY_KEY);
      if (locationStored) {
        const locationParsed = JSON.parse(locationStored) as {
          trolleyId: string;
          locationId?: string;
          locationBarcode?: string;
        } | null;

        if (locationParsed && locationParsed.trolleyId === trolleyId) {
          // Update context with registered trolley ID and flag to open modal
          sessionStorage.setItem(
            PENDING_LOCATION_TROLLEY_KEY,
            JSON.stringify({
              trolleyId: idToNavigate, // Use registered trolley ID
              locationId: locationParsed.locationId,
              locationBarcode: locationParsed.locationBarcode,
              openModal: true, // Flag to indicate modal should be opened
              segment: "Trolley", // Ensure Trolley tab is selected
            })
          );
          const locationToNavigate =
            locationParsed.locationBarcode || locationParsed.locationId || "";
          if (locationToNavigate) {
            navigate(
              `/location-info/${encodeURIComponent(locationToNavigate)}`
            );
            return;
          }
        }
      }
    } catch (error) {
      console.warn("Failed to resume pending location trolley context", error);
    }

    // Check for pending trolley load context (loading tote to trolley)
    try {
      const stored = sessionStorage.getItem(PENDING_TROLLEY_LOAD_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as
          | {
              toteId: string;
              trolleyId: string;
            }
          | undefined;

        if (parsed && parsed.trolleyId === trolleyId) {
          sessionStorage.setItem(
            PENDING_TROLLEY_LOAD_KEY,
            JSON.stringify({
              toteId: parsed.toteId,
              trolleyId: idToNavigate, // Use registered trolley ID
            })
          );
          navigate(`/tote-info/${encodeURIComponent(parsed.toteId)}`);
          return;
        }
      }
    } catch (error) {
      console.warn("Failed to resume pending trolley load flow", error);
    }

    // Fallback: navigate to trolley info screen - use record ID
    navigate(`/trolley-info/${encodeURIComponent(idToNavigate)}`);
  };

  return (
    <div className={Styles.mainContainer}>
      <Header />
      <div className={Styles.contentContainer}>
        <div className={Styles.header}>
          <Icon component={LeftIcon} onClick={() => navigate(-1)} />
          <h2 className={Styles.title}>Review Trolley Information</h2>
        </div>

        <div className={Styles.infoSection}>
          <Card className={Styles.infoCard}>
            <div className={Styles.trolleyInfoRow}>
              <Icon component={TrolleyIcon} className={Styles.icon} />
              <div>
                <div className={Styles.label}>Trolley ID</div>
                <div className={Styles.value}>{trolleyId}</div>
              </div>
            </div>

            <Row gutter={[16, 0]} style={{ marginTop: "16px" }}>
              <Col span={12}>
                <div className={Styles.detailSection}>
                  <div className={Styles.label}>MDU</div>
                  <div className={Styles.value}>{mdu || "N/A"}</div>
                </div>
              </Col>
              <Col span={12}>
                <div className={Styles.detailSection}>
                  <div className={Styles.label}>Status</div>
                  <div className={`${Styles.value} ${Styles.statusAvailable}`}>
                    Empty - Available
                  </div>
                </div>
              </Col>
            </Row>
          </Card>
        </div>

        <div className={Styles.bottomAction}>
          <Button
            className={Styles.registerButton}
            size="large"
            onClick={handleRegister}
            loading={registerMutation.isPending}
          >
            Register Trolley
          </Button>
        </div>

        <SuccessAlert
          open={isSuccessOpen}
          title="Trolley registered successfully"
          message={
            <>
              The Trolley (<b style={{ color: "#101010" }}>ID: {trolleyId}</b>)
              has been successfully registered and is ready to load.
            </>
          }
          buttonText="Done"
          onClose={handleSuccessClose}
        />
      </div>
    </div>
  );
};

export default RegisterTrolleyReview;
